import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight } from "lucide-react";

interface PersonalInfo {
  fullName: string;
  email: string;
  phone: string;
  currentRole: string;
  organization: string;
  yearsInField: string;
  fieldOfExpertise: string;
  website: string;
  additionalInfo: string;
}

interface EB1ACriteria {
  majorPrizes: string;
  professionalMemberships: string;
  publishedMaterial: string;
  judgingWork: string;
  originalContributions: string;
  scholarlyArticles: string;
  artisticExhibitions: string;
  leadingRole: string;
  highSalary: string;
  commercialSuccess: string;
  continueWork: string;
  documentation: string;
}

const EB1AAssessmentForm = () => {
  const { toast } = useToast();
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    fullName: "",
    email: "",
    phone: "",
    currentRole: "",
    organization: "",
    yearsInField: "",
    fieldOfExpertise: "",
    website: "",
    additionalInfo: ""
  });
  const [formData, setFormData] = useState<EB1ACriteria>({
    majorPrizes: "",
    professionalMemberships: "",
    publishedMaterial: "",
    judgingWork: "",
    originalContributions: "",
    scholarlyArticles: "",
    artisticExhibitions: "",
    leadingRole: "",
    highSalary: "",
    commercialSuccess: "",
    continueWork: "",
    documentation: ""
  });

  const handlePersonalInfoChange = (field: keyof PersonalInfo, value: string) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleChange = (field: keyof EB1ACriteria, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // Validate personal information
    const requiredFields: (keyof PersonalInfo)[] = ['fullName', 'email', 'phone', 'currentRole', 'fieldOfExpertise'];
    const missingFields = requiredFields.filter(field => !personalInfo[field]);
    
    if (missingFields.length > 0) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required personal information fields.",
      });
      return;
    }

    // Count the number of "yes" responses
    const criteriaCount = Object.entries(formData)
      .filter(([key]) => key !== "continueWork" && key !== "documentation")
      .filter(([_, value]) => value === "yes").length;

    if (formData.continueWork !== "yes") {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "You must confirm that you will continue working in your field of extraordinary ability.",
      });
      return;
    }

    if (formData.documentation !== "yes") {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "You must confirm that you can provide supporting documentation.",
      });
      return;
    }

    const result = {
      eligibility: criteriaCount >= 3,
      criteriaCount,
      personalInfo,
      formData
    };

    console.log("Assessment result:", result);
    
    toast({
      title: criteriaCount >= 3 ? "Potentially Eligible" : "More Evidence Needed",
      description: criteriaCount >= 3 
        ? "Based on your responses, you may qualify for EB-1A classification. Please proceed with document collection."
        : "You currently meet fewer than 3 criteria. Consider gathering more evidence or exploring alternative visa pathways.",
    });
  };

  const renderPersonalInfoFields = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Personal Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name *</Label>
          <Input
            id="fullName"
            value={personalInfo.fullName}
            onChange={(e) => handlePersonalInfoChange('fullName', e.target.value)}
            placeholder="Enter your full name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={personalInfo.email}
            onChange={(e) => handlePersonalInfoChange('email', e.target.value)}
            placeholder="Enter your email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            value={personalInfo.phone}
            onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
            placeholder="Enter your phone number"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="currentRole">Current Role *</Label>
          <Input
            id="currentRole"
            value={personalInfo.currentRole}
            onChange={(e) => handlePersonalInfoChange('currentRole', e.target.value)}
            placeholder="Enter your current role"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="organization">Organization</Label>
          <Input
            id="organization"
            value={personalInfo.organization}
            onChange={(e) => handlePersonalInfoChange('organization', e.target.value)}
            placeholder="Enter your organization"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="yearsInField">Years in Field</Label>
          <Input
            id="yearsInField"
            value={personalInfo.yearsInField}
            onChange={(e) => handlePersonalInfoChange('yearsInField', e.target.value)}
            placeholder="Enter years of experience"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="fieldOfExpertise">Field of Expertise *</Label>
          <Input
            id="fieldOfExpertise"
            value={personalInfo.fieldOfExpertise}
            onChange={(e) => handlePersonalInfoChange('fieldOfExpertise', e.target.value)}
            placeholder="Enter your field of expertise"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website/Portfolio</Label>
          <Input
            id="website"
            value={personalInfo.website}
            onChange={(e) => handlePersonalInfoChange('website', e.target.value)}
            placeholder="Enter your website URL"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="additionalInfo">Additional Information</Label>
        <Textarea
          id="additionalInfo"
          value={personalInfo.additionalInfo}
          onChange={(e) => handlePersonalInfoChange('additionalInfo', e.target.value)}
          placeholder="Any additional information you'd like to share"
          className="min-h-[100px]"
        />
      </div>
    </div>
  );

  const renderCriterion = (
    field: keyof EB1ACriteria,
    label: string,
    description?: string
  ) => (
    <div className="space-y-4">
      <div>
        <Label className="text-base">{label}</Label>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      <RadioGroup
        value={formData[field]}
        onValueChange={(value) => handleChange(field, value)}
        className="flex flex-col space-y-1"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="yes" id={`${field}-yes`} />
          <Label htmlFor={`${field}-yes`}>Yes</Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="no" id={`${field}-no`} />
          <Label htmlFor={`${field}-no`}>No</Label>
        </div>
      </RadioGroup>
    </div>
  );

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>EB-1A: Extraordinary Ability Assessment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Personal Information Section */}
        {renderPersonalInfoFields()}

        {/* Primary Criteria */}
        <div className="space-y-6 border-t pt-6">
          <h3 className="text-lg font-semibold">Eligibility Criteria</h3>
          
          {renderCriterion(
            "majorPrizes",
            "1. Major Prizes or Awards",
            "Have you received nationally or internationally recognized prizes/awards for excellence?"
          )}

          {renderCriterion(
            "professionalMemberships",
            "2. Professional Association Memberships",
            "Are you a member of organizations requiring outstanding achievements?"
          )}

          {renderCriterion(
            "publishedMaterial",
            "3. Published Material About You",
            "Has your work been featured in professional publications or major media?"
          )}

          {renderCriterion(
            "judgingWork",
            "4. Judging Others' Work",
            "Have you served as a judge evaluating others' work in your field?"
          )}

          {renderCriterion(
            "originalContributions",
            "5. Original Contributions",
            "Have you made original scientific, scholarly, or business contributions of major significance?"
          )}

          {renderCriterion(
            "scholarlyArticles",
            "6. Scholarly Articles",
            "Have you authored scholarly articles in professional publications?"
          )}

          {renderCriterion(
            "artisticExhibitions",
            "7. Artistic Exhibitions/Showcases",
            "Have your works been displayed at exhibitions/showcases? (if applicable)"
          )}

          {renderCriterion(
            "leadingRole",
            "8. Leading/Critical Role",
            "Have you performed in a leading/critical role for distinguished organizations?"
          )}

          {renderCriterion(
            "highSalary",
            "9. High Salary/Remuneration",
            "Is your compensation significantly higher than others in your field?"
          )}

          {renderCriterion(
            "commercialSuccess",
            "10. Commercial Success",
            "Have you achieved commercial success in the performing arts? (if applicable)"
          )}
        </div>

        {/* Additional Requirements */}
        <div className="space-y-6 border-t pt-6">
          {renderCriterion(
            "continueWork",
            "Will you continue working in your field of extraordinary ability in the U.S.?",
            "This is required for EB-1A eligibility"
          )}

          {renderCriterion(
            "documentation",
            "Can you provide supporting documentation?",
            "References, news coverage, awards, etc."
          )}
        </div>

        <Button 
          onClick={handleSubmit}
          className="w-full"
          size="lg"
        >
          Submit Assessment
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default EB1AAssessmentForm;